import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDownRight, MapPin } from "lucide-react";

const HERO_IMG = "/work-4.webp";

const LINES = ["Speciality Forensic", "Cleaning &", "Property Restoration."];

const lineParent = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.12, delayChildren: 0.35 } },
};
const lineChild = {
  hidden: { y: "110%" },
  visible: { y: 0, transition: { duration: 1, ease: [0.16, 1, 0.3, 1] } },
};

export default function Hero({ onNav }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const overlay = useTransform(scrollYProgress, [0, 1], [0.5, 0.85]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative flex min-h-screen items-end overflow-hidden"
    >
      <motion.div style={{ y, scale }} className="absolute inset-0 z-0">
        <img
          src={HERO_IMG}
          alt=""
          className="h-full w-full object-cover opacity-80 grayscale"
        />
      </motion.div>
      <motion.div
        style={{ opacity: overlay }}
        className="absolute inset-0 z-[1] bg-[#050505]"
      />
      <div className="absolute inset-0 z-[2] bg-gradient-to-t from-[#050505] via-transparent to-[#050505]/60" />

      <div className="relative z-10 mx-auto w-full max-w-[1400px] px-5 pb-16 pt-44 md:px-10 md:pb-24 md:pt-56">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.15 }}
          className="mb-8 flex items-center gap-3 text-xs uppercase tracking-[0.28em] text-[#c4a962]"
        >
          <MapPin className="h-4 w-4" />
          Protecting people, property, and privacy.
        </motion.div>

        <motion.h1
          variants={lineParent}
          initial="hidden"
          animate="visible"
          className="font-display text-[13vw] font-semibold leading-[0.95] tracking-tighter text-[#f5f5f0] md:text-[8.5vw] lg:text-[7.5rem]"
        >
          {LINES.map((line, idx) => (
            <span key={idx} className="block overflow-hidden pb-[0.08em]">
              <motion.span variants={lineChild} className="block">
                {idx === 1 ? (
                  <>
                    Cleaning <span className="text-[#c4a962]">&amp;</span>
                  </>
                ) : (
                  line
                )}
              </motion.span>
            </span>
          ))}
        </motion.h1>

        <div className="mt-10 grid grid-cols-1 gap-8 border-t border-[#2a2a2a] pt-8 md:grid-cols-12">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9 }}
            className="max-w-xl text-base leading-relaxed text-[#8a8a8a] md:col-span-7 md:text-lg"
          >
            Professional Remediation and Restoration Services servicing Regional
            Victoria and NSW
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1 }}
            className="flex flex-col gap-3 md:col-span-5 md:items-end"
          >
            <button
              onClick={() => onNav("#contact")}
              data-testid="hero-quote-btn"
              className="group flex w-full items-center justify-between gap-4 bg-[#f5f5f0] px-6 py-4 text-sm font-semibold uppercase tracking-wide text-[#050505] transition-colors duration-300 hover:bg-[#c4a962] md:w-auto"
            >
              Request a quote
              <ArrowDownRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1 group-hover:translate-y-1" />
            </button>
            <button
              onClick={() => onNav("#services")}
              data-testid="hero-services-btn"
              className="w-full border border-[#2a2a2a] px-6 py-4 text-sm font-medium uppercase tracking-wide text-[#f5f5f0] transition-colors duration-300 hover:border-[#c4a962] hover:text-[#c4a962] md:w-auto"
            >
              Explore our services
            </button>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
